import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * External App Cart Integration
 * Monitors for external app cart additions and triggers the theme's cart system
 */
class ExternalAppCartIntegration {
  constructor() {
    this.isProcessing = false;
    this.observer = null;
    this.lastCartItemCount = 0;
    this.cartDrawerWasClosedByUser = false;
    this.init();
  }

  init() {
    // Get initial cart count
    this.updateCartCount();
    
    // Monitor for actual cart changes by watching for network requests
    this.monitorCartRequests();
    
    // Listen for cart drawer close events
    this.listenForCartClose();
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.lastCartItemCount = cart.item_count;
    } catch (error) {
      // Silent fail
    }
  }

  listenForCartClose() {
    // Listen for cart drawer close events
    document.addEventListener('click', (e) => {
      const target = e.target;
      if (target && target.classList && (target.classList.contains('cart-drawer__close') || 
                    target.closest('.cart-drawer__close') ||
                    target.classList.contains('drawer-overlay'))) {
        this.cartDrawerWasClosedByUser = true;
      }
    });
  }


  monitorCartRequests() {
    // Store the original fetch function
    const originalFetch = window.fetch;
    
    // Override fetch to monitor cart-related requests
    window.fetch = async (...args) => {
      const response = await originalFetch.apply(window, args);
      
      // Check if this is a cart add request (not just cart.js GET requests)
      const url = args[0];
      const method = args[1]?.method || 'GET';
      
      if (typeof url === 'string' && url.includes('/cart/add') && method === 'POST') {
        // Check if the request was successful and came from Teeinblue
        if (response.ok && !document.documentElement.hasAttribute('data-direct-checkout-pending') && this.isTeeinblueRequest()) {
          
          // Check if cart count actually increased
          const currentCartResponse = await fetch('/cart.js');
          const currentCart = await currentCartResponse.json();
          
          if (currentCart.item_count > this.lastCartItemCount && !this.cartDrawerWasClosedByUser) {
            this.lastCartItemCount = currentCart.item_count;
            // Small delay to ensure cart is updated
            setTimeout(() => {
              if (!this.isProcessing) {
                this.handleExternalCartAdd();
              }
            }, 100);
          }
          
          // Reset the close flag after successful add
          this.cartDrawerWasClosedByUser = false;
        }
      }
      
      return response;
    };
  }

  isTeeinblueRequest() {
    // Check if Teeinblue elements are present on the page
    return document.querySelector('.tee-campaign') || 
           document.querySelector('.tee-product') ||
           document.querySelector('#teeinblue-app');
  }


  async handleExternalCartAdd() {
    if (this.isProcessing) return;
    
    this.isProcessing = true;

    try {
      // Fetch the current cart to get updated data
      const cartResponse = await fetch('/cart.js', {
        ...fetchConfig(),
        method: 'GET'
      });

      if (!cartResponse.ok) {
        throw new Error('Failed to fetch cart data');
      }

      const cartData = await cartResponse.json();

      // Fetch cart sections to ensure proper rendering with preserved styling context
      const sectionsToRender = ['cart-drawer', 'cart-icon-bubble'];
      const sectionsResponse = await fetch(`${window.location.pathname}?${sectionsToRender.map(section => `sections=${section}`).join('&')}`);
      const sectionsData = await sectionsResponse.json();
      let sections = sectionsData;

      // Dispatch the CartAddEvent to trigger the theme's cart update mechanism
      const cartAddEvent = new CartAddEvent(cartData, 'external-app', {
        source: 'external-app-integration',
        itemCount: cartData.item_count,
        productId: this.getLastAddedProductId(cartData),
        sections: sections,
        didError: false
      });

      document.dispatchEvent(cartAddEvent);

      // Open the cart drawer
      this.openCartDrawer();

      // Hide the external app success message after a delay
      setTimeout(() => {
        this.hideExternalSuccessMessage();
      }, 2000);

    } catch (error) {
      // Silent fail
    } finally {
      // Reset processing flag after a delay to prevent rapid firing
      setTimeout(() => {
        this.isProcessing = false;
      }, 1000);
    }
  }

  getLastAddedProductId(cartData) {
    // Try to get the product ID from the last item in the cart
    if (cartData.items && cartData.items.length > 0) {
      const lastItem = cartData.items[cartData.items.length - 1];
      return lastItem.product_id?.toString() || '';
    }
    return '';
  }

  openCartDrawer() {
    if (document.documentElement.hasAttribute('data-direct-checkout-pending')) return;
    // Find and trigger the cart drawer
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    } else {
      // Fallback: try to find and click the cart button
      const cartButton = document.querySelector('[data-testid="cart-icon"]') || 
                        document.querySelector('.header-actions__action.action__cart');
      if (cartButton) {
        cartButton.click();
      }
    }
  }

  hideExternalSuccessMessage() {
    const successElement = document.querySelector('.tee-form-responses');
    if (successElement) {
      successElement.style.transition = 'opacity 0.3s ease';
      successElement.style.opacity = '0';
      setTimeout(() => {
        successElement.remove();
      }, 300);
    }
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

// Initialize the integration when the DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ExternalAppCartIntegration();
  });
} else {
  new ExternalAppCartIntegration();
}

export default ExternalAppCartIntegration;
